import React from "react";
import "./RolePanel.css";

const ROLE_DESCRIPTIONS = {
  oracle: "Predicts the next color draw with ~55% accuracy",
  seer: "Sees ahead in 4-ball blocks (red-minus-blue difference)",
  pessimist: "Estimates when the game will end (with noise)",
  early_game_specialist: "Knows the first 10 draws upfront",
  late_game_specialist:
    "Predicts next color when leader has gap >= 2 (~90% accuracy)",
};

const COLOR_LABELS = {
  red: "Red",
  blue: "Blue",
  green: "Green",
  yellow: "Yellow",
};

function ColorBall({ color, size = "normal" }) {
  if (!color) return null;

  return (
    <div className={`color-ball color-ball-${color} color-ball-${size}`}>
      <span>{COLOR_LABELS[color] || color}</span>
    </div>
  );
}

function SignalMeta({ children }) {
  return <div className="signal-meta">{children}</div>;
}

function OracleSignal({ signal }) {
  if (!signal) return null;

  return (
    <div className="role-signal-card">
      <div className="signal-row">
        <ColorBall color={signal.predicted_next_color} />
        <div>
          <div className="signal-headline">Predicted next draw</div>
          <SignalMeta>For tick {signal.target_tick}</SignalMeta>
        </div>
      </div>
    </div>
  );
}

function LateGameSignal({ signal }) {
  if (!signal) return null;

  return (
    <div className="role-signal-card">
      <div className="signal-row">
        <ColorBall color={signal.predicted_next_color} />
        <div>
          <div className="signal-headline">Late-game prediction</div>
          <SignalMeta>For tick {signal.target_tick}</SignalMeta>
        </div>
      </div>
    </div>
  );
}

function SeerSignal({ signal }) {
  if (!signal) return null;

  const value = signal.red_minus_blue;
  const valueClass =
    value > 0 ? "positive" : value < 0 ? "negative" : "neutral";
  const formattedValue = value > 0 ? `+${value}` : `${value}`;

  return (
    <div className="role-signal-card">
      <div className="seer-grid">
        <div>
          <div className={`seer-value ${valueClass}`}>{formattedValue}</div>
          <SignalMeta>Red minus Blue</SignalMeta>
        </div>
        <div className="signal-side-info">
          <div className="signal-headline">
            Draws {signal.block_start_tick}-{signal.block_end_tick}
          </div>
          <SignalMeta>{signal.block_size} draw block</SignalMeta>
        </div>
      </div>
    </div>
  );
}

function PessimistSignal({ signal }) {
  if (!signal) return null;

  return (
    <div className="role-signal-card">
      <div className="pessimist-estimate">
        <div className="estimate-value">{signal.observed_end_tick_estimate}</div>
        <SignalMeta>Estimated final draw number</SignalMeta>
      </div>
      <SignalMeta>
        Error range: {signal.error_support?.min} to {signal.error_support?.max}
      </SignalMeta>
    </div>
  );
}

function EarlyGamePreview({ draws }) {
  if (!draws || draws.length === 0) return null;

  return (
    <div className="role-signal-card">
      <div className="signal-headline">Known first 10 draws</div>
      <div className="ball-sequence">
        {draws.map((color, index) => (
          <div key={`${color}-${index}`} className="ball-sequence-item">
            <ColorBall color={color} size="small" />
            <span className="ball-index">{index + 1}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function renderSignal(role, signal) {
  if (!signal) return <div className="role-empty">No new signal this tick.</div>;

  switch (role) {
    case "oracle":
      return <OracleSignal signal={signal} />;
    case "seer":
      return <SeerSignal signal={signal} />;
    case "pessimist":
      return <PessimistSignal signal={signal} />;
    case "late_game_specialist":
      return <LateGameSignal signal={signal} />;
    default:
      return null;
  }
}

function renderHistory(role, signals) {
  if (!signals || signals.length === 0) {
    return <div className="role-empty">No signal history yet.</div>;
  }

  const reversed = [...signals].reverse();
  return (
    <div className="signal-list">
      {reversed.map((signal, index) => (
        <div key={signals.length - index} className="signal-item">
          <div className="signal-item-label">Signal #{signals.length - index}</div>
          {renderSignal(role, signal)}
        </div>
      ))}
    </div>
  );
}

function RolePanel({ roleInfo }) {
  if (!roleInfo) return null;

  const { role, tick, new_revelation, visible_state, initial_information } = roleInfo;
  const roleName = role.replace(/_/g, " ");
  const description = ROLE_DESCRIPTIONS[role] || "";
  const signals = visible_state?.signals || [];
  const knownFirstDraws = initial_information?.known_first_10_draws || [];
  const displayedSignal = new_revelation ?? (signals.length > 0 ? signals[signals.length - 1] : null);

  return (
    <div className="role-panel">
      <div className="role-header">
        <div className="role-badge">Your Role</div>
        <h3 className="role-name">{roleName}</h3>
        <p className="role-description">{description}</p>
      </div>

      {role === "early_game_specialist" && (
        <div className="role-initial-info">
          <div className="signal-label">Initial Information</div>
          <EarlyGamePreview draws={knownFirstDraws} />
        </div>
      )}

      {role !== "early_game_specialist" && (
        <div className="role-latest-signal">
          <div className="signal-label">Latest Signal — Draw {tick}</div>
          {displayedSignal
            ? renderSignal(role, displayedSignal)
            : <div className="role-empty">Waiting for first signal...</div>}
        </div>
      )}

      {role !== "early_game_specialist" && (
        <div className="role-signal-history">
          <div className="signal-label">Signal History ({signals.length})</div>
          {renderHistory(role, signals)}
        </div>
      )}
    </div>
  );
}

export default RolePanel;
